import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input } from '@/components/ui';
import { updateDeal } from '@/data/client';
import type { Deal } from '@/data/types';
import { FormError } from './Modal';

export const todayIso = () => new Date().toLocaleDateString('en-CA');
export const longDate = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/* When to come back to this deal, and why. Works on lost deals too — that is
   how a "call back in the fall" lands on someone's Today list. */
export function FollowUpCard({ deal }: { deal: Deal }) {
  const qc = useQueryClient();
  const [date, setDate] = useState(deal.followUpAt ?? '');
  const [note, setNote] = useState(deal.followUpNote ?? '');
  useEffect(() => { setDate(deal.followUpAt ?? ''); setNote(deal.followUpNote ?? ''); }, [deal.followUpAt, deal.followUpNote]);
  const save = useMutation({
    mutationFn: (v: { followUpAt: string | null; followUpNote: string | null }) => updateDeal(deal.id, v),
    onSuccess: () => { for (const k of ['deal', 'deals', 'followUps']) void qc.invalidateQueries({ queryKey: [k] }); },
  });
  const changed = date !== (deal.followUpAt ?? '') || note !== (deal.followUpNote ?? '');
  const due = deal.followUpAt && deal.followUpAt <= todayIso();
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head">
        <h3>Follow-up</h3>
        {deal.followUpAt && <span className={`crm-followups__when ${due ? 'is-due' : ''}`}>{due ? 'Due' : 'On'} {longDate(deal.followUpAt)}</span>}
      </div>
      <div className="crm-form">
        <Input label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <Input label="Why" placeholder="e.g. Asked for a call after the busy season" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
        <div className="crm-composer__foot">
          {deal.followUpAt
            ? <Button size="sm" variant="ghost" disabled={save.isPending} onClick={() => save.mutate({ followUpAt: null, followUpNote: null })}>Done — clear</Button>
            : <span />}
          <Button size="sm" disabled={!changed || !date || save.isPending} onClick={() => save.mutate({ followUpAt: date, followUpNote: note.trim() || null })}>Save</Button>
        </div>
        <FormError error={save.error} />
      </div>
    </Card>
  );
}
